import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { fmtTRY, fmtDate } from "@/lib/format";
import { PageHeader, StatCard, SectionCard, EmptyState, StickyPageHead } from "@/components/Common";
import SubscriptionBanner from "@/components/SubscriptionBanner";
import SetupOnboarding from "@/components/setup/SetupOnboarding";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  PieChart, Pie, Cell,
} from "recharts";
import { Trophy, AlertTriangle } from "lucide-react";

// "Bu Ayki Finansal Dağılım" halkası için — üç kategori.  Renkler
// Penz brand chart palette (2026-10 color-system revision):
//   Satış (positive)   → #97C216 accent lime
//   Satın Alma (main)  → #055473 primary petrol
//   Gider (secondary)  → #939AA6 cool gray
// Red/orange for real semantic errors only — dekoratif turuncu kaldırıldı.
const MONTHLY_SEGMENT_COLORS = {
  sales: "#97C216",     // Penz lime
  purchases: "#055473", // Penz primary petrol
  expenses: "#939AA6",  // Penz cool gray
};

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-slate-500 text-sm">Yükleniyor…</div>;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <StickyPageHead>
        <PageHeader
          title="Gösterge Paneli"
          subtitle={`Bugün: ${fmtDate(new Date().toISOString())}`}
        />
      </StickyPageHead>

      <SetupOnboarding />
      <SubscriptionBanner subtle />

      {/*
        "Bu Ayki Finansal Dağılım" halkası + KPI grid'i.  Referans
        görseldeki büyük sol kart + sağdaki KPI dizilimi gibi:
        - lg ekranlarda halka sola pin'lenir (2 kolon eninde),
        - KPI'lar sağ tarafta 2x3 grid olarak sıralanır.
        Mobilde halka tam genişlik, KPI'lar altta 2x3 devam eder.
      */}
      {/*
        Referans layout (2026-11):
          SOL:  "Bu Ayki Finansal Dağılım" tek büyük kart — sağ blokta
                (2 sıra KPI + 1 sıra müşteri kartı) toplam yüksekliğine
                stretch olur.
          SAĞ:  3 kolon × 2 sıra KPI (kompakt) + altında 2 kolon müşteri
                kartları.  KPI'lar dikey stretch etmez; sol kart onların
                doğal toplam yüksekliğine göre uzar (grid items-stretch).
        Mobilde tek kolona iner: finansal → 6 KPI → 2 müşteri.
      */}
      <div className="grid grid-cols-1 lg:grid-cols-10 gap-4" data-testid="tour-dashboard-top">
        <div className="lg:col-span-3">
          <MonthlyFinancialBreakdown
            sales={data.month_sales || 0}
            purchases={data.month_purchases || 0}
            expenses={data.month_expenses || 0}
          />
        </div>
        <div className="lg:col-span-7 flex flex-col gap-4">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="dashboard-kpis">
            <StatCard label="Toplam Satış" amount={data.total_sales} accent="slate" testId="kpi-total-sales" />
            <StatCard label="Toplam Tahsilat" amount={data.total_collected} accent="emerald" testId="kpi-total-collected" />
            <StatCard label="Bekleyen Alacak" amount={data.outstanding_receivables} hint={`Vadesi Geçen: ${fmtTRY(data.overdue_receivables)}`} accent="amber" testId="kpi-outstanding-recv" />
            <StatCard label="Toplam Satın Alma" amount={data.total_purchases} accent="slate" testId="kpi-total-purchases" />
            <StatCard label="Bekleyen Borç" amount={data.outstanding_payables} hint={`Vadesi Geçen: ${fmtTRY(data.overdue_payables)}`} accent="amber" testId="kpi-outstanding-pay" />
            <StatCard label="Net Kâr (Toplam)" amount={data.net_profit} accent={data.net_profit >= 0 ? "emerald" : "red"} testId="kpi-net-profit" />
          </div>
          {/* En Çok Satış Yapılan Müşteri + En Borçlu Müşteri — SAĞ kolon
              içinde 2 eşit sütun; ana grid'in altına düşürülmez ki
              soldaki Finansal Dağılım kartı bu satırın altına kadar
              stretch olabilsin. */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <HighlightCard
              icon={Trophy}
              title="En Çok Satış Yapılan Müşteri"
              data={data.top_customer}
              accent="emerald"
              link={data.top_customer ? `/customers/${data.top_customer.id}` : null}
              valueLabel="Toplam Satış"
              valueKey="total_sales"
              testId="highlight-top-customer"
            />
            <HighlightCard
              icon={AlertTriangle}
              title="En Borçlu Müşteri"
              data={data.most_indebted_customer}
              accent="amber"
              link={data.most_indebted_customer ? `/customers/${data.most_indebted_customer.id}` : null}
              valueLabel="Bekleyen Alacak"
              valueKey="outstanding"
              testId="highlight-most-indebted"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <SectionCard title="Aylık Satış / Satın Alma / Kâr (Son 6 Ay)" className="lg:col-span-2" testId="chart-trend">
          <div className="p-5">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.trend} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="label" stroke="#94a3b8" axisLine={false} tickLine={false} />
                <YAxis stroke="#94a3b8" axisLine={false} tickLine={false}
                  tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`} />
                <Tooltip formatter={(v) => fmtTRY(v)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="sales" name="Satış" fill="#97C216" radius={[4, 4, 0, 0]} />
                <Bar dataKey="purchases" name="Satın Alma" fill="#939AA6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="profit" name="Kâr" fill="#055473" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        <SectionCard
          title="Vadesi Yaklaşan Alacaklar"
          testId="upcoming-receivables"
          action={
            <div className="text-xs text-slate-500 shrink-0 ml-3">
              Toplam:{" "}
              <span className="text-sm font-semibold text-slate-900 tabular-nums" data-testid="upcoming-receivables-total">
                {fmtTRY((data.upcoming_receivables ?? []).reduce((s, r) => s + (Number(r.remaining) || 0), 0))}
              </span>
            </div>
          }
        >
          {(data.upcoming_receivables ?? []).length === 0 ? (
            <EmptyState title="Bekleyen alacak yok" />
          ) : (
            <ul className="divide-y divide-slate-100">
              {(data.upcoming_receivables ?? []).map((r) => (
                <li key={r.id} className="px-5 py-3 flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{r.customer_name}</div>
                    <div className="text-xs text-slate-500 truncate">{r.name || "—"}</div>
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    <div className="text-sm font-semibold text-slate-900">{fmtTRY(r.remaining)}</div>
                    <div className="text-[11px] text-slate-500">{fmtDate(r.due_date)}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard
          title="Vadesi Geçen Alacaklar"
          testId="overdue-receivables"
          action={
            <div className="text-xs text-slate-500 shrink-0 ml-3">
              Toplam:{" "}
              <span className="text-sm font-semibold text-red-600 tabular-nums" data-testid="overdue-receivables-total">
                {fmtTRY((data.overdue_receivables_list ?? []).reduce((s, r) => s + (Number(r.remaining) || 0), 0))}
              </span>
            </div>
          }
        >
          {(data.overdue_receivables_list ?? []).length === 0 ? (
            <EmptyState title="Vadesi geçen alacak yok" />
          ) : (
            <ul className="divide-y divide-slate-100">
              {(data.overdue_receivables_list ?? []).map((r) => (
                <li key={r.id} className="px-5 py-3 flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{r.customer_name}</div>
                    <div className="text-xs text-slate-500 truncate">{r.name || "—"}</div>
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    <div className="text-sm font-semibold text-red-600">{fmtTRY(r.remaining)}</div>
                    <div className="text-[11px] text-red-600">Vade: {fmtDate(r.due_date)}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>

        <SectionCard
          title="Vadesi Geçen Borçlar"
          testId="overdue-payables"
          action={
            <div className="text-xs text-slate-500 shrink-0 ml-3">
              Toplam:{" "}
              <span className="text-sm font-semibold text-red-600 tabular-nums" data-testid="overdue-payables-total">
                {fmtTRY((data.overdue_payables_list ?? []).reduce((s, p) => s + (Number(p.remaining) || 0), 0))}
              </span>
            </div>
          }
        >
          {(data.overdue_payables_list ?? []).length === 0 ? (
            <EmptyState title="Vadesi geçen borç yok" />
          ) : (
            <ul className="divide-y divide-slate-100">
              {(data.overdue_payables_list ?? []).map((p) => (
                <li key={p.id} className="px-5 py-3 flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{p.vendor_name}</div>
                    <div className="text-xs text-slate-500 truncate">{p.description || "—"}</div>
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    <div className="text-sm font-semibold text-red-600">{fmtTRY(p.remaining)}</div>
                    <div className="text-[11px] text-red-600">Vade: {fmtDate(p.due_date)}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
      </div>
    </div>
  );
}

function HighlightCard({ icon: Icon, title, data, accent, link, valueLabel, valueKey, testId }) {
  const accentBg = {
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    blue: "bg-blue-50 text-blue-700",
  }[accent];
  const inner = (
    <div className="bg-white border border-slate-200 rounded-xl p-5 hover:border-slate-300 transition-colors" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{title}</div>
          <div className="font-display text-xl font-bold text-slate-900 mt-2 truncate">{data?.name || "—"}</div>
          {data && <div className="text-xs text-slate-500 mt-1">{valueLabel}: <span className="font-medium text-slate-700">{fmtTRY(data[valueKey])}</span></div>}
        </div>
        <div className={`w-10 h-10 rounded-xl ${accentBg} flex items-center justify-center shrink-0`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
  return link ? <Link to={link}>{inner}</Link> : inner;
}

/**
 * Bu ayki üç finansal kategorinin (Satış / Satın Alma / Gider) toplam
 * pastasını halka olarak gösterir.  Grafik dekoratif değil — her dilim
 * kategori tutarına orantılıdır.  Yüzdeler yuvarlama sonrası %100'e
 * eşitlenir (son kategori kalanı alır).
 *
 * Toplam sıfırsa nötr halka + boş durum mesajı gösterir.
 */
function MonthlyFinancialBreakdown({ sales, purchases, expenses }) {
  const total = (sales || 0) + (purchases || 0) + (expenses || 0);
  const empty = total <= 0;
  // Yüzdeleri hesapla; kalan payı son (görünen) kategoriye yaz.
  const rawPct = empty ? { sales: 0, purchases: 0, expenses: 0 } : {
    sales:     Math.round(((sales || 0) / total) * 1000) / 10,
    purchases: Math.round(((purchases || 0) / total) * 1000) / 10,
    expenses:  Math.round(((expenses || 0) / total) * 1000) / 10,
  };
  if (!empty) {
    const sum = rawPct.sales + rawPct.purchases + rawPct.expenses;
    const diff = Math.round((100 - sum) * 10) / 10;
    if (diff !== 0) {
      // Son gösterilen (sıfırdan büyük) kategoriye kalanı ekle.
      const order = ["expenses", "purchases", "sales"];
      const target = order.find((k) => rawPct[k] > 0) || "sales";
      rawPct[target] = Math.round((rawPct[target] + diff) * 10) / 10;
    }
  }

  const chartData = empty
    ? [{ name: "Bu ay", value: 1, empty: true }]
    : [
        { name: "Satış",       value: sales,     key: "sales" },
        { name: "Satın Alma",  value: purchases, key: "purchases" },
        { name: "Gider",       value: expenses,  key: "expenses" },
      ].filter((s) => s.value > 0);

  const rows = [
    { key: "sales",     label: "Satış",       amount: sales,     pct: rawPct.sales },
    { key: "purchases", label: "Satın Alma",  amount: purchases, pct: rawPct.purchases },
    { key: "expenses",  label: "Gider",       amount: expenses,  pct: rawPct.expenses },
  ];

  const fmtPct = (v) => {
    if (empty) return "—";
    // Ondalık gereksizse 0'ı gizle: "%40" vs "%39.6"
    const rounded = Math.round(v * 10) / 10;
    const text = Number.isInteger(rounded) ? `${rounded}` : `${rounded.toFixed(1)}`;
    return `%${text.replace(".", ",")}`;
  };

  // Donut merkezindeki dinamik ay-yıl (Türkçe locale, örn "Eylül 2026").
  const monthLabel = new Intl.DateTimeFormat("tr-TR", { month: "long", year: "numeric" })
    .format(new Date())
    .replace(/^./, (c) => c.toLocaleUpperCase("tr-TR"));

  return (
    <div
      className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 lg:p-6 h-full flex flex-col"
      data-testid="dashboard-monthly-breakdown"
    >
      <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
        Bu Ayki Finansal Dağılım
      </div>

      {/* Donut + satırlar dikey akış. Donut kart genişliğine göre
          ``clamp`` ile ölçeklenir → geniş desktop'ta ~240px, dar laptop
          / kart daraldığında ~180px'e kadar iner.  aspect-square 1:1'i
          garantiler; kart dışına taşımaz.  Merkezdeki metin de
          clamp-tabanlı font ile küçülür (kırpılmaz).  Max 240 seçildi
          çünkü sağdaki KPI (2 sıra) + müşteri (1 sıra) toplam yüksekliği
          ~394px; sol kartın yatay stretch değil, ALT KENAR HİZASI eşit
          olması için içeriğin bu yükseklikte kalması şart. */}
      <div className="flex-1 flex flex-col items-center justify-center gap-3 mt-3">
        <div
          className="relative aspect-square shrink-0"
          style={{ width: "clamp(160px, 55%, 230px)" }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                dataKey="value"
                innerRadius="70%"
                outerRadius="100%"
                startAngle={90}
                endAngle={-270}
                stroke="none"
                paddingAngle={empty ? 0 : 2}
                isAnimationActive={false}
              >
                {chartData.map((entry, idx) => (
                  <Cell
                    key={idx}
                    fill={entry.empty
                      ? "#e2e8f0"  // slate-200
                      : MONTHLY_SEGMENT_COLORS[entry.key]}
                  />
                ))}
              </Pie>
              {!empty && (
                <Tooltip
                  formatter={(v) => fmtTRY(v)}
                  contentStyle={{ borderRadius: 8, borderColor: "#e2e8f0", fontSize: 12 }}
                />
              )}
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center px-2 max-w-[75%]">
              <div
                className="uppercase tracking-[0.18em] text-slate-500 font-semibold"
                style={{ fontSize: "clamp(9px, 1.4vw, 11px)" }}
              >
                {monthLabel}
              </div>
              {!empty && (
                <div
                  className="font-display font-semibold text-slate-900 mt-1 tabular-nums whitespace-nowrap"
                  style={{ fontSize: "clamp(12px, 1.8vw, 16px)" }}
                >
                  {fmtTRY(total)}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Kategori satırları: 3 kolon grid — [dot+Label] | Tutar | %.
            Label kolonu `minmax(0,1fr)` ile şrink olur ama truncate
            YOK: text-slate-700 nowrap kalır; sıkışırsa tutar ve yüzde
            "auto" kolonlarıyla yer alır.  Kartın padding'i mobilde
            azalır → toplam satır genişliği rahat. */}
        <div className="w-full space-y-2">
          {empty ? (
            <div className="text-sm text-slate-500 text-center" data-testid="dashboard-monthly-breakdown-empty">
              Bu ay henüz finansal hareket yok.
            </div>
          ) : (
            rows.map((r) => (
              <div
                key={r.key}
                className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-x-3 text-sm text-slate-700"
                data-testid={`dashboard-monthly-breakdown-row-${r.key}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: MONTHLY_SEGMENT_COLORS[r.key] }}
                  />
                  <span className="font-medium whitespace-nowrap">{r.label}</span>
                </div>
                <div className="tabular-nums whitespace-nowrap text-right">{fmtTRY(r.amount)}</div>
                <div className="tabular-nums whitespace-nowrap text-right text-slate-500 min-w-[3rem]">{fmtPct(r.pct)}</div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
